/* eslint-disable prefer-promise-reject-errors */
import { useLocales } from '@/Locales';
import { existUser } from '@/services/sys';
import { getVerifyCode, httpLogin, httpSystemResetPassword } from '@/services/user';
import { validIsMobile } from '@/utils';
import { removeToken, setToken } from '@/utils/cookies';
import { validIsPassword } from '@/utils/validate';
import { Button, Form, Input, message, Result } from '@sinohealth/butterfly-ui-components';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import styles from './index.less';

const iconPwd = <span className={classNames(styles['form-icon'], 'iconfont icon-a-2')} />;

const sendCodeTimeOut = 60;

interface ForgetFormProps {
  loading?: boolean;
  onLogin: (params: any) => Promise<void>;
  onBack?: () => void;
}

const ForgetForm: React.FC<ForgetFormProps> = ({ loading, onLogin, onBack }) => {
  const [form] = Form.useForm();
  const [phoneNo, setPhoneNo] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [loadSendCode, setLoadSendCode] = useState(false);
  const [canSendCode, setCanSendCode] = useState(true);
  const [hasSendCode, setHasSendCode] = useState(false);
  const [existUserFlag, setExistUserFlag] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [countdownSeconds, setCountdownSeconds] = useState(5); // 倒计时秒数

  const { t } = useLocales();

  // 倒计时逻辑 - 当到达成功页面时开始倒计时
  useEffect(() => {
    let countdownTimer: NodeJS.Timeout;

    if (currentStep === 3) {
      // 重置倒计时为5秒
      setCountdownSeconds(5);

      countdownTimer = setInterval(() => {
        setCountdownSeconds((prev) => {
          if (prev <= 1) {
            // 倒计时结束，自动跳转到登录页
            clearInterval(countdownTimer);
            onBack?.();
            removeToken();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    // 清理定时器
    return () => {
      if (countdownTimer) {
        clearInterval(countdownTimer);
      }
    };
  }, [currentStep, onBack]);

  useEffect(() => {
    if (timeLeft > 0) {
      setCanSendCode(false);
      setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 900);
    } else {
      setCanSendCode(true);
    }
  }, [timeLeft]);

  const handleConfirm = () => {
    if (currentStep === 1) {
      form
        .validateFields()
        .then(async (values) => {
          const res: any = await httpLogin({ ...values, channel: 'phone' });
          if (res.token) {
            setToken(res.token);
            setCurrentStep(2);
          }
        })
        .catch((err) => {
          console.log(err);
          // setTimeout(() => {
          //   if (err.errorFields) {
          //     setErrorMsg(err.errorFields[0].errors[0]);
          //   } else {
          //     err?.retMsg && message.error(err?.retMsg);
          //   }
          // });
        });
    } else {
      form
        .validateFields()
        .then(async (values) => {
          await httpSystemResetPassword(values.password);
          removeToken();
          setCurrentStep(3);
        })
        .catch((err) => {
          console.log(err);
          // setErrorMsg(err?.errorFields?.[0]?.errors?.[0] || '');
        });
    }
  };

  const handleSendSmsCode = async () => {
    if (!phoneNo) {
      message.error(t('手机号码不能为空'));
      return;
    }

    // 确保手机号格式正确且用户存在（通过Form校验）
    try {
      await form.validateFields(['phone']);
    } catch (error) {
      // 如果校验失败，不发送验证码
      return;
    }

    try {
      setLoadSendCode(true);
      await getVerifyCode(phoneNo);
      setHasSendCode(true);
      setTimeLeft(sendCodeTimeOut);
      setCanSendCode(false);
    } catch (error) {
      console.error('发送验证码失败:', error);
      message.error(t('发送验证码失败，请稍后重试'));
    } finally {
      setLoadSendCode(false);
    }
  };

  const validPhoneNo = async (rule: any, value: any) => {
    if (!value || value === '') {
      return Promise.reject(t('手机号码不能为空'));
    }
    if (value.length < 11) {
      return Promise.reject(t('请输入11位手机号码'));
    }
    if (value.length >= 11 && !validIsMobile(value)) {
      message.error(t('手机号格式不正确'));
      return Promise.reject(t('手机号格式不正确'));
    }
    return Promise.resolve();
  };

  /**
   * 检查手机号是否存在
   * @param phone 手机号
   */
  const checkPhoneExists = async (phone: string) => {
    try {
      const userExists: any = await existUser({ phone });
      setExistUserFlag(userExists);

      // 显示用户反馈
      if (!userExists) {
        message.error('该手机号未进行注册');
      }

      return userExists;
    } catch (error) {
      console.error('检查用户存在性失败:', error);
      setExistUserFlag(false);

      return false;
    }
  };

  const validCaptcha = (rule: any, value: any) => {
    if (!value) {
      message.error(t('验证码不能为空'));
      return Promise.reject(t('验证码不能为空'));
    }
    if (value.length > 4) {
      message.error(t('请输入4位验证码'));
      return Promise.reject(t('请输入4位验证码'));
    }
    return Promise.resolve();
  };

  const SuccessStep = () => {
    return (
      <Result
        status="success"
        className={styles.successStep}
        title={t('密码重置成功')}
        subTitle={`${countdownSeconds}${t('秒后自动跳转至登录页')}`}
        extra={[
          <Button
            type="primary"
            size="large"
            key="console"
            block
            onClick={() => {
              onBack?.();
              removeToken();
            }}
          >
            {t('立即登录')}
          </Button>,
        ]}
      />
    );
  };

  // 步骤条组件
  const StepIndicator = () => {
    const steps = [
      { key: 1, title: t('验证手机号码') },
      { key: 2, title: t('设置新密码') },
      { key: 3, title: t('密码重置成功') },
    ];

    return (
      <div className={styles.stepIndicator}>
        {steps.map((step, index) => (
          <div key={step.key} className={styles.stepItem}>
            <div className={styles.stepContent}>
              <div
                className={classNames(styles.stepCircle, currentStep >= step.key ? styles.active : styles.inactive)}
              />
              <span
                className={classNames(styles.stepTitle, currentStep >= step.key ? styles.activeText : styles.inactiveText)}
              >
                {step.title}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={classNames(styles.stepLine, {
                  [styles.firstLine]: index === 0,
                  [styles.secondLine]: currentStep === 2,
                  [styles.activeLine]: currentStep > step.key,
                  [styles.inactiveLine]: currentStep <= step.key,
                })}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  const AccountFormItem = () => {
    return (
      <>
        <Form.Item name="phone" rules={[{ required: true, validator: validPhoneNo }]}>
          <Input
            className={styles['form-input']}
            size="large"
            placeholder={t('请输入登录手机号')}
            maxLength={11}
            prefix={<span className={classNames(styles['form-icon'], 'iconfont icon-a-3')} />}
            onChange={(e) => {
              const phone = e.target.value;
              setPhoneNo(phone);
              // 手机号格式正确后检查是否存在
              if (validIsMobile(phone)) {
                checkPhoneExists(phone);
              } else {
                setExistUserFlag(false);
              }
            }}
          />
        </Form.Item>
        <Form.Item name="captcha" rules={[{ required: true, validator: validCaptcha }]}>
          <Input
            className={styles['form-input']}
            size="large"
            placeholder={t('请输入登录验证码')}
            maxLength={4}
            prefix={<span className={classNames(styles['form-icon'], 'iconfont icon-a-4')} />}
            suffix={
              <Button
                type="link"
                size="small"
                style={{
                  maxWidth: 120,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                disabled={!canSendCode || !validIsMobile(phoneNo) || !existUserFlag}
                onClick={() => handleSendSmsCode()}
              >
                {canSendCode
                  ? t(hasSendCode ? '重新获取验证码' : '获取验证码')
                  : `${timeLeft}s${t('后重新获取')}`}
              </Button>
            }
          />
        </Form.Item>
      </>
    );
  };

  const PasswordFormItem = () => {
    return (
      <>
        <Form.Item
          name="password"
          label={<div className={styles.label}>{t('登录密码')}：</div>}
          rules={[
            {
              required: true,
              validator: (rule: any, value: any) => {
                if (!value) {
                  return Promise.reject(t('密码不能为空'));
                }
                if (value.length >= 8 && validIsPassword(value)) {
                  return Promise.resolve();
                }
                if (value.length >= 8 && !validIsPassword(value)) {
                  message.error(t('必须由8-16位数字+字母+特殊字符(!@#%&)组成'));
                  return Promise.reject(t('必须由8-16位数字+字母+特殊字符(!@#%&)组成'));
                }
                return Promise.reject(t('必须由8-16位数字+字母+特殊字符(!@#%&)组成'));
              },
            },
          ]}
        >
          <Input.Password
            prefix={iconPwd}
            placeholder={t('请输入登录密码')}
            size="large"
            autoComplete="new-password"
            className={styles.inputItem}
            visibilityToggle={{
              visible: showPwd,
              onVisibleChange: setShowPwd,
            }}
          />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label={<div className={styles.label}>{t('确认密码')}：</div>}
          rules={[
            {
              required: true,
              validator: (rule: any, value: any) => {
                if (!value) {
                  return Promise.reject(t('确认密码不能为空'));
                }
                if (value === form.getFieldValue('password')) {
                  return Promise.resolve();
                }
                if (value.length >= 8) {
                  message.error(t('两次密码需一致'));
                }
                return Promise.reject(t('两次密码需一致'));
              },
            },
          ]}
        >
          <Input.Password
            prefix={iconPwd}
            placeholder={t('请输入确认密码')}
            size="large"
            autoComplete="new-password"
            className={styles.inputItem}
            visibilityToggle={{
              visible: showConfirmPwd,
              onVisibleChange: setShowConfirmPwd,
            }}
          />
        </Form.Item>
      </>
    );
  };

  const SubmitButton = () => {
    return (
      <>
        <Form.Item>
          <Button
            type="primary"
            className={styles.loginBtn}
            loading={loading || loadSendCode}
            size="large"
            onClick={handleConfirm}
          >
            {currentStep === 1 ? t('下一步') : t('确定')}
          </Button>
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <div
            className={styles.backBtn}
            onClick={() => {
              if (currentStep === 1) {
                onBack?.();
              } else {
                setCurrentStep(1);
              }
            }}
          >
            {currentStep === 1 ? t('返回账号登录') : t('上一步')}
          </div>
        </Form.Item>
      </>
    );
  };

  return (
    <div className={styles.forgetForm}>
      {currentStep !== 3 ? (
        <>
          <div className={styles.forgetTitle}>{t('重置密码')}</div>
          <StepIndicator />
          <Form form={form} layout="vertical" className={styles.loginForm}>
            {currentStep === 1 ? AccountFormItem() : null}
            {currentStep === 2 ? PasswordFormItem() : null}
            {SubmitButton()}
            {/* <div
              className={styles.errorMsg}
              style={{ visibility: errorMsg ? 'visible' : 'hidden' }}
            >
              {errorMsg || '手机号码格式不正确，请重新输入！'}
            </div> */}
          </Form>
        </>
      ) : null}
      {currentStep === 3 ? <SuccessStep /> : null}
    </div>
  );
};

export default ForgetForm;
